/**
 * Pure helpers for service-template selection and application.
 *
 * Kept free of Vue/component state so they can be unit-tested with Node's built-in
 * test runner (see system-templates.spec.js) and shared between the Integration
 * Builder wizard and the template-selection modal.
 */

import { AUTH_TYPES, TEMPLATE_CATEGORIES } from './constants.js'

/**
 * Resolve the request URL / HTTP method to pre-fill when a service template is applied.
 *
 * The endpoint the admin actually picked (selectedEndpoint, falling back to serviceEndpoint,
 * then endpoints[0]) is the source of truth. Its `url` is a complete URL template used
 * verbatim: either fully absolute, or carrying a {placeholder}/[TOKEN] the admin replaces at
 * setup (e.g. {instance_url}/crm/v3/objects/contacts). It is never joined onto
 * defaults.endpointAddress - the catalogue holds no bare relative paths, and prefixing would
 * corrupt every placeholder URL. defaults.endpointAddress only stands in when the endpoint
 * itself carries no url (e.g. a fixed-host template whose complete URL lives in the defaults).
 *
 * @param {Object} serviceTemplate - Template object, optionally carrying selectedEndpoint/serviceEndpoint
 * @returns {{endpoint: Object|null, requestUrl: string|null, httpMethod: string|null}}
 */
export function resolveEndpointPrefill (serviceTemplate) {
	if (!serviceTemplate || !Array.isArray(serviceTemplate.endpoints) || serviceTemplate.endpoints.length === 0) {
		return { endpoint: null, requestUrl: null, httpMethod: null }
	}

	const endpoint = serviceTemplate.selectedEndpoint || serviceTemplate.serviceEndpoint || serviceTemplate.endpoints[0]
	const baseAddress = serviceTemplate.defaults && serviceTemplate.defaults.endpointAddress
		? serviceTemplate.defaults.endpointAddress
		: null

	return {
		endpoint,
		requestUrl: (endpoint && endpoint.url) || baseAddress || null,
		httpMethod: endpoint && endpoint.method ? endpoint.method.toUpperCase() : null
	}
}

/**
 * Whether a service template may be offered against the given system integration.
 *
 * A template qualifies when it has endpoints to offer AND belongs to the same system template
 * (exact id match). Notifier templates (Slack Webhook, PagerDuty) are tagged GENERIC but are no
 * longer cross-listed on other systems: an admin uses them by creating a system from their own
 * card, so a vendor system (or another notifier's system) only ever lists its own endpoints.
 *
 * @param {Object} template - Candidate service template
 * @param {Object} systemTemplate - Template the system integration was created from
 * @returns {boolean}
 */
export function isTemplateCompatible (template, systemTemplate) {
	if (!template || !Array.isArray(template.endpoints) || template.endpoints.length === 0) {
		return false
	}
	if (systemTemplate && template.id === systemTemplate.id) {
		return true
	}
	return false
}

// Mirrors the worker's token grammar (LegacyTokenReplacer): the field is any run of non-]
// characters (lazy, so the optional :INT suffix - any casing - only matches literally), because a
// token this validator cannot parse is a token that fails at runtime instead of at authoring
// time. The optional :INT suffix marks a typed numeric position: the worker validates the value
// as an integer and emits it as a bare JSON number. The token is written quoted in templates, so
// a requestBody carrying one is still strict valid JSON here.
const PLACEHOLDER_PATTERN = /\[PLACEHOLDER_([^\]]+?)(?::[Ii][Nn][Tt])?\]/g

/**
 * Validate the template catalogue. Returns a flat list of human-readable problems;
 * an empty array means the catalogue is clean.
 *
 * Checks per template: guid present and unique, category drawn from TEMPLATE_CATEGORIES,
 * defaults.authenticationTypeId drawn from AUTH_TYPES. Checks per endpoint: headers use
 * {Name, value} key casing, requestBody parses as JSON, and every [PLACEHOLDER_X] token
 * in the requestBody or url matches an entry in that endpoint's fields[] (case-insensitively).
 *
 * @param {Array} templates - Template catalogue (e.g. SYSTEM_TEMPLATES)
 * @returns {Array<string>} Problems found, empty when clean
 */
export function validateTemplates (templates) {
	const errors = []
	if (!Array.isArray(templates)) {
		return ['catalogue is not an array']
	}

	const guidOwners = new Map()
	const validCategories = new Set(Object.values(TEMPLATE_CATEGORIES))
	const validAuthTypeIds = new Set(Object.values(AUTH_TYPES))

	for (const template of templates) {
		const label = template.id || template.name || '<unnamed template>'

		if (!template.guid) {
			errors.push(`${label}: missing guid`)
		} else if (guidOwners.has(template.guid)) {
			errors.push(`${label}: guid duplicates ${guidOwners.get(template.guid)}`)
		} else {
			guidOwners.set(template.guid, label)
		}

		if (template.category && !validCategories.has(template.category)) {
			errors.push(`${label}: unknown category "${template.category}"`)
		}

		const authTypeId = template.defaults ? template.defaults.authenticationTypeId : undefined
		if (authTypeId !== undefined && !validAuthTypeIds.has(authTypeId)) {
			errors.push(`${label}: unknown defaults.authenticationTypeId ${authTypeId}`)
		}

		// The wizard maps (defaults.notes || description) into config.API_Integrations.Notes,
		// which the backend caps at 500 characters - an over-length value fails system creation.
		const effectiveNotes = (template.defaults && template.defaults.notes) || template.description || ''
		if (effectiveNotes.length > 500) {
			errors.push(`${label}: notes/description is ${effectiveNotes.length} characters; the backend caps Notes at 500`)
		}

		const hasBaseAddress = !!(template.defaults && template.defaults.endpointAddress)

		for (const endpoint of template.endpoints || []) {
			const endpointLabel = `${label} :: ${endpoint.name || endpoint.url || '<unnamed endpoint>'}`

			// The endpoint must resolve to a request URL when applied: either its own url, or the
			// template's defaults.endpointAddress standing in for a url-less endpoint. Neither present
			// means resolveEndpointPrefill yields null and the wizard pre-fills a blank URL.
			if (!endpoint.url && !hasBaseAddress) {
				errors.push(`${endpointLabel}: no url and no defaults.endpointAddress to fall back on`)
			}

			for (const header of endpoint.headers || []) {
				if (!('Name' in header) || !('value' in header)) {
					errors.push(`${endpointLabel}: header keys must be {Name, value}, got {${Object.keys(header).join(', ')}}`)
				}
			}

			if (endpoint.requestBody) {
				try {
					JSON.parse(endpoint.requestBody)
				} catch (error) {
					errors.push(`${endpointLabel}: requestBody is not parseable JSON (${error.message})`)
				}
			}

			// The worker renders endpoint.url through the same token replacer as the body, so both
			// are orphan-checked. Comparison is case-insensitive, matching the worker's field
			// resolution.
			const fieldNames = new Set((endpoint.fields || []).map(field => String(field.name).toUpperCase()))
			for (const source of [endpoint.requestBody, endpoint.url]) {
				if (!source) continue
				for (const match of source.matchAll(PLACEHOLDER_PATTERN)) {
					if (!fieldNames.has(match[1].toUpperCase())) {
						errors.push(`${endpointLabel}: ${match[0]} has no matching entry in fields[]`)
					}
				}
			}
		}
	}

	return errors
}
